import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Radar, AlertTriangle, TrendingUp, TrendingDown, Minus, Activity,
  Shield, Plane, Wrench, Users, Cloud, Radio, Building2, Zap,
  Brain, RefreshCw, Bell, CheckCircle2, Eye, BarChart3, Target,
  Loader2, AlertCircle, ChevronRight
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

export default function RiskRadarPage() {
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const [snapshot, setSnapshot] = useState(null);
  const [domains, setDomains] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [predictions, setPredictions] = useState(null);
  const [trends, setTrends] = useState({});
  const [loading, setLoading] = useState(true);
  const [generatingPredictions, setGeneratingPredictions] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const fetchSnapshot = async () => {
    try {
      const res = await fetch(`${API_URL}/api/risk-radar/snapshot`, { headers: apiHeaders });
      if (res.ok) setSnapshot(await res.json());
    } catch (err) {
      console.error('Error fetching snapshot:', err);
    }
  };

  const fetchDomains = async () => {
    try {
      const res = await fetch(`${API_URL}/api/risk-radar/domains`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setDomains(data.domains || []);
      }
    } catch (err) {
      console.error('Error fetching domains:', err);
    }
  };

  const fetchHeatmap = async () => {
    try {
      const res = await fetch(`${API_URL}/api/risk-radar/heatmap`, { headers: apiHeaders });
      if (res.ok) setHeatmap(await res.json());
    } catch (err) {
      console.error('Error fetching heatmap:', err);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/risk-radar/alerts?limit=20`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  };

  const fetchTrends = async () => {
    try {
      const metrics = ['incidents', 'hazards', 'near_misses'];
      const trendData = {};
      for (const metric of metrics) {
        const res = await fetch(`${API_URL}/api/risk-radar/trends/${metric}?months=6`, { headers: apiHeaders });
        if (res.ok) trendData[metric] = await res.json();
      }
      setTrends(trendData);
    } catch (err) {
      console.error('Error fetching trends:', err);
    }
  };

  const generateAIPredictions = async () => {
    setGeneratingPredictions(true);
    try {
      const res = await fetch(`${API_URL}/api/risk-radar/ai/predict`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        setPredictions(data);
        toast.success('AI predictions generated');
        fetchAlerts();
      } else {
        toast.error('Failed to generate predictions');
      }
    } catch (err) {
      toast.error('Error generating predictions');
    } finally {
      setGeneratingPredictions(false);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/risk-radar/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success('Alert acknowledged');
        fetchAlerts();
      }
    } catch (err) {
      toast.error('Error acknowledging alert');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSnapshot(), fetchDomains(), fetchHeatmap(), fetchAlerts(), fetchTrends()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const getThreatLevelColor = (level) => {
    const colors = {
      critical: 'bg-red-500 text-white',
      high: 'bg-orange-500 text-white',
      elevated: 'bg-yellow-500 text-black',
      moderate: 'bg-blue-500 text-white',
      low: 'bg-green-500 text-white'
    };
    return colors[level] || colors.low;
  };

  const getThreatLevelBorder = (level) => {
    const colors = {
      critical: 'border-red-500/50',
      high: 'border-orange-500/50',
      elevated: 'border-yellow-500/50',
      moderate: 'border-blue-500/50',
      low: 'border-green-500/50'
    };
    return colors[level] || colors.low;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'increasing') return <TrendingUp className="w-4 h-4 text-red-400" />;
    if (trend === 'decreasing') return <TrendingDown className="w-4 h-4 text-green-400" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getDomainIcon = (domain) => {
    const icons = {
      flight_operations: Plane,
      ground_operations: Building2,
      maintenance: Wrench,
      cabin_safety: Users,
      security: Shield,
      cargo: Building2,
      training: Users,
      fatigue: Activity,
      weather: Cloud,
      atc: Radio,
      airports: Building2,
      organizational: Building2
    };
    const Icon = icons[domain] || Activity;
    return <Icon className="w-5 h-5" />;
  };

  const getHeatmapCellColor = (value) => {
    if (value >= 80) return 'bg-red-500';
    if (value >= 60) return 'bg-orange-500';
    if (value >= 40) return 'bg-yellow-500';
    if (value >= 20) return 'bg-blue-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6" data-testid="risk-radar-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Radar className="w-8 h-8 text-cyan-400" />
          <div>
            <h1 className="text-2xl font-bold text-slate-100">{t('riskRadar.title')}</h1>
            <p className="text-sm text-slate-400">{t('riskRadar.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { fetchSnapshot(); fetchDomains(); fetchAlerts(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('common.refresh')}
          </Button>
          <Button onClick={generateAIPredictions} disabled={generatingPredictions} className="bg-purple-600 hover:bg-purple-700">
            {generatingPredictions ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
            {generatingPredictions ? t('ai.analyzing') : 'AI Predict'}
          </Button>
        </div>
      </div>

      {/* Overall Risk Score */}
      <Card className={`bg-slate-800/50 border-2 ${getThreatLevelBorder(snapshot?.overall_threat_level)} mb-6`}>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-sm text-slate-400 mb-2">Overall Risk Score</p>
              <div className="text-5xl font-bold text-slate-100">{snapshot?.overall_risk_score?.toFixed(0) || 0}</div>
              <Badge className={`mt-2 ${getThreatLevelColor(snapshot?.overall_threat_level)}`}>
                {snapshot?.overall_threat_level?.toUpperCase() || 'LOW'}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-3">Key Metrics</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-300">Active Hazards</span>
                  <span className="font-bold text-yellow-400">{snapshot?.total_hazards || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Incidents (MTD)</span>
                  <span className="font-bold text-orange-400">{snapshot?.total_incidents_mtd || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Near Misses (MTD)</span>
                  <span className="font-bold text-blue-400">{snapshot?.total_near_misses_mtd || 0}</span>
                </div>
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-3">Action Items</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-300">Overdue Actions</span>
                  <span className={`font-bold ${snapshot?.overdue_actions > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {snapshot?.overdue_actions || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Active Alerts</span>
                  <span className="font-bold text-purple-400">{alerts?.filter(a => !a.acknowledged).length || 0}</span>
                </div>
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-3">AI Forecast (7 days)</p>
              {predictions?.risk_forecast_7d ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-slate-100">{predictions.risk_forecast_7d.predicted_score?.toFixed(0)}</span>
                    {getTrendIcon(predictions.risk_forecast_7d.trend)}
                  </div>
                  <p className="text-xs text-slate-400">Confidence: {predictions.risk_forecast_7d.confidence}%</p>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Click "AI Predict" to generate</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 mb-6">
          <TabsTrigger value="overview">Domain Overview</TabsTrigger>
          <TabsTrigger value="heatmap">Risk Heat Map</TabsTrigger>
          <TabsTrigger value="alerts">Predictive Alerts ({alerts?.filter(a => !a.acknowledged).length || 0})</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {domains.map(domain => (
              <Card key={domain.domain} className={`bg-slate-800/50 border ${getThreatLevelBorder(domain.threat_level)}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getDomainIcon(domain.domain)}
                      <span className="font-medium text-slate-200 capitalize">{domain.domain?.replace(/_/g, ' ')}</span>
                    </div>
                    <Badge className={getThreatLevelColor(domain.threat_level)}>{domain.threat_level}</Badge>
                  </div>
                  <div className="mb-3">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-slate-400">Risk Score</span>
                      <span className="font-bold text-slate-100">{domain.risk_score?.toFixed(0)}/100</span>
                    </div>
                    <Progress value={domain.risk_score} className="h-2" />
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center p-2 bg-slate-900/50 rounded">
                      <p className="text-yellow-400 font-bold">{domain.active_hazards}</p>
                      <p className="text-slate-500">Hazards</p>
                    </div>
                    <div className="text-center p-2 bg-slate-900/50 rounded">
                      <p className="text-orange-400 font-bold">{domain.open_incidents}</p>
                      <p className="text-slate-500">Incidents</p>
                    </div>
                    <div className="text-center p-2 bg-slate-900/50 rounded">
                      <p className="text-red-400 font-bold">{domain.overdue_actions}</p>
                      <p className="text-slate-500">Overdue</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="heatmap">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-slate-100">{heatmap?.title}</CardTitle>
              <CardDescription>{heatmap?.data_period}</CardDescription>
            </CardHeader>
            <CardContent>
              {heatmap?.cells && heatmap.cells.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="p-2 text-left text-slate-400"></th>
                        {heatmap.col_labels?.map(col => (
                          <th key={col.id} className="p-2 text-center text-slate-300 text-sm">{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {heatmap.row_labels?.map(row => (
                        <tr key={row.id}>
                          <td className="p-2 text-slate-300 text-sm">{row.label}</td>
                          {heatmap.col_labels?.map(col => {
                            const cell = heatmap.cells.find(c => c.row_id === row.id && c.col_id === col.id);
                            return (
                              <td key={col.id} className="p-1">
                                <div 
                                  className={`w-full h-12 rounded flex items-center justify-center text-white font-bold ${getHeatmapCellColor(cell?.value || 0)}`}
                                  title={`${row.label} / ${col.label}: ${cell?.count || 0} incidents`}
                                >
                                  {cell?.count || 0}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No heatmap data available</p>
              )}
              <div className="flex items-center justify-center gap-4 mt-4 text-xs">
                <div className="flex items-center gap-1"><div className="w-4 h-4 bg-green-500 rounded"></div> Low</div>
                <div className="flex items-center gap-1"><div className="w-4 h-4 bg-blue-500 rounded"></div> Moderate</div>
                <div className="flex items-center gap-1"><div className="w-4 h-4 bg-yellow-500 rounded"></div> Elevated</div>
                <div className="flex items-center gap-1"><div className="w-4 h-4 bg-orange-500 rounded"></div> High</div>
                <div className="flex items-center gap-1"><div className="w-4 h-4 bg-red-500 rounded"></div> Critical</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts">
          <div className="space-y-4">
            {predictions?.analysis_summary && (
              <Card className="bg-purple-500/10 border-purple-500/30">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Brain className="w-5 h-5 text-purple-400 mt-1" />
                    <div>
                      <h4 className="font-medium text-purple-300 mb-1">AI Analysis Summary</h4>
                      <p className="text-sm text-purple-200">{predictions.analysis_summary}</p>
                      {predictions.top_risk_areas && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {predictions.top_risk_areas.map((area, i) => (
                            <Badge key={i} variant="outline" className="text-purple-300 border-purple-500/30">{area}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {alerts.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No predictive alerts. Click "AI Predict" to generate.</p>
            ) : (
              alerts.map(alert => (
                <Card key={alert.id} className={`bg-slate-800/50 border ${getThreatLevelBorder(alert.threat_level)} ${alert.acknowledged ? 'opacity-60' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getThreatLevelColor(alert.threat_level)}>{alert.threat_level}</Badge>
                          <Badge variant="outline">{alert.domain?.replace(/_/g, ' ')}</Badge>
                          <Badge variant="outline">{alert.time_horizon}</Badge>
                          {alert.acknowledged && <Badge className="bg-slate-600">Acknowledged</Badge>}
                        </div>
                        <h4 className="font-medium text-slate-100 mb-1">{alert.title}</h4>
                        <p className="text-sm text-slate-400 mb-3">{alert.description}</p>
                        
                        {alert.contributing_factors?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs text-slate-500 mb-1">Contributing Factors:</p>
                            <div className="flex flex-wrap gap-1">
                              {alert.contributing_factors.map((f, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{f}</Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {alert.recommended_actions?.length > 0 && (
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Recommended Actions:</p>
                            <ul className="text-sm text-slate-300 list-disc list-inside">
                              {alert.recommended_actions.slice(0, 3).map((a, i) => (
                                <li key={i}>{a}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      {!alert.acknowledged && (
                        <Button variant="outline" size="sm" onClick={() => acknowledgeAlert(alert.id)}>
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Acknowledge
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="trends">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(trends).map(([metric, data]) => (
              <Card key={metric} className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-slate-100 capitalize flex items-center gap-2">
                    {metric.replace(/_/g, ' ')}
                    {getTrendIcon(data?.trend)}
                  </CardTitle>
                  <CardDescription>
                    {data?.trend === 'increasing' ? 'Increasing' : data?.trend === 'decreasing' ? 'Decreasing' : 'Stable'}
                    {data?.trend_percentage ? ` (${data.trend_percentage.toFixed(1)}%)` : ''}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-32 flex items-end gap-1">
                    {data?.data_points?.map((dp, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-cyan-500/50 rounded-t"
                          style={{ height: `${Math.max(10, (dp.value / Math.max(...data.data_points.map(d => d.value || 1))) * 100)}%` }}
                        ></div>
                        <span className="text-xs text-slate-500 mt-1">{dp.date?.split('-')[1]}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
